import { CheckCircle2, Loader2, Mail } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useAuth } from '@/lib/auth-context'

const POLL_INTERVAL_MS = 3_000
const COOLDOWN_SECONDS = 30

interface StepVerifyEmailProps {
  onVerified: () => void
}

export function StepVerifyEmail({ onVerified }: StepVerifyEmailProps): React.JSX.Element {
  const { user, refetchAccount } = useAuth()
  const [cooldown, setCooldown] = useState(0)
  const [sending, setSending] = useState(false)
  const [verified, setVerified] = useState(false)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Poll for email verification
  useEffect(() => {
    if (verified) return

    intervalRef.current = setInterval(async () => {
      await refetchAccount()
    }, POLL_INTERVAL_MS)

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [refetchAccount, verified])

  // Watch for emailVerified change
  useEffect(() => {
    if (user?.emailVerified) {
      setVerified(true)
      if (intervalRef.current) clearInterval(intervalRef.current)
      // Brief delay so user sees the success state
      const timeout = setTimeout(onVerified, 1200)
      return () => clearTimeout(timeout)
    }
  }, [user?.emailVerified, onVerified])

  // Cooldown timer
  useEffect(() => {
    if (cooldown <= 0) return
    const timer = setInterval(() => {
      setCooldown((prev) => (prev <= 1 ? 0 : prev - 1))
    }, 1000)
    return () => clearInterval(timer)
  }, [cooldown])

  async function handleSendAgain(): Promise<void> {
    setSending(true)
    try {
      const baseUrl = import.meta.env.VITE_API_BASE_URL ?? '/api'
      await fetch(`${baseUrl}/auth/send-verification-email`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user?.email }),
      })
    } catch {
      // Best effort
    }
    setSending(false)
    setCooldown(COOLDOWN_SECONDS)
  }

  if (verified) {
    return (
      <div className="flex flex-col items-center gap-4 py-8">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-green-50">
          <CheckCircle2 className="h-7 w-7 text-green-600" />
        </div>
        <h2 className="text-xl font-semibold text-text-primary">Email verified</h2>
        <p className="text-sm text-text-secondary">Setting up your account...</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center gap-5 py-6">
      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50">
        <Mail className="h-7 w-7 text-brand-500" />
      </div>

      <div className="text-center">
        <h2 className="text-xl font-semibold text-text-primary">Check your inbox</h2>
        <p className="mt-2 text-sm text-text-secondary">We sent a verification email to</p>
        <p className="mt-1 font-medium text-text-primary">{user?.email}</p>
        <p className="mt-3 text-sm text-text-secondary">
          Click the link in the email to verify your address and continue.
        </p>
      </div>

      <div className="flex items-center gap-2 text-xs text-text-tertiary">
        <Loader2 className="h-3 w-3 animate-spin" />
        <span>Waiting for verification...</span>
      </div>

      <button
        type="button"
        onClick={handleSendAgain}
        disabled={cooldown > 0 || sending}
        className="rounded-lg border border-surface-3 bg-surface-0 px-4 py-2 text-sm font-medium text-brand-600 hover:bg-surface-1 disabled:text-text-tertiary disabled:cursor-not-allowed transition-colors"
      >
        {sending ? 'Sending...' : cooldown > 0 ? `Send again (${cooldown}s)` : 'Send again'}
      </button>

      <p className="text-xs text-text-tertiary">Check your spam folder if you don&apos;t see it.</p>
    </div>
  )
}
